import { useState } from "react";
import type { PortalProblemRecord } from "@/lib/portal-runtime";
import { formatRecordCost } from "@/lib/problem-record-presentation";
import {
  INVESTIGATION_STAGES,
  INVESTIGATION_UNAVAILABLE,
  INVESTIGATION_VIEWS,
  type InvestigationView,
} from "@/lib/investigation-context";

/** All views stay scoped to the selected API record; unavailable reads never use fixtures. */
export function InvestigationContext({ record }: { record: PortalProblemRecord }) {
  const [view, setView] = useState<InvestigationView>("overview");
  const selected = INVESTIGATION_VIEWS.find((item) => item.key === view)!;
  return (
    <section
      className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-6"
      aria-label="Investigation workspace"
    >
      <p className="text-xs text-muted-foreground">Selected investigation: {record.displayId}</p>
      <nav className="mt-3 flex flex-wrap gap-2" aria-label="Investigation views">
        {INVESTIGATION_VIEWS.map((item) => (
          <button
            key={item.key}
            type="button"
            aria-pressed={view === item.key}
            onClick={() => setView(item.key)}
            className={`rounded-md border px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${view === item.key ? "border-primary/40 bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <section className="mt-6" aria-labelledby="investigation-view-heading">
        <h2 id="investigation-view-heading" className="text-lg font-semibold">
          {selected.label}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{selected.question}</p>
        {view === "overview" ? (
          <>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <ContextFact
                label="Source"
                value={record.sourceSummary ?? "Unavailable — source not supplied"}
              />
              <ContextFact
                label="Classification"
                value={
                  record.classification?.replaceAll("_", " ") ??
                  "Unavailable — classification not supplied"
                }
              />
              <ContextFact
                label="Freshness"
                value={record.freshness ?? "Unknown — freshness not supplied"}
              />
              <ContextFact
                label="Quality"
                value={record.quality ?? "Unknown — quality not supplied"}
              />
              <ContextFact
                label="Technical stage"
                value="Unavailable — stage projection not returned"
              />
              <ContextFact
                label="Effectiveness"
                value="Unavailable — outcome evidence not returned"
              />
            </dl>
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              Review the supplied source, cutoff, and reconciliation before interpreting the
              summary. Use these contextual views to inspect capability boundaries without leaving
              this investigation. Live changes and approvals remain disabled.
            </p>
          </>
        ) : (
          <>
            <div className="mt-4 rounded-lg border border-dashed border-border bg-background p-4">
              <h3 className="font-semibold">Record detail unavailable — read-only</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {INVESTIGATION_UNAVAILABLE[view]}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                No demonstration evidence is substituted and no save or approval action is offered.
              </p>
            </div>
            {view === "evidence" && (
              <dl>
                <ContextFact
                  label="Linked claims (aggregate only)"
                  value={
                    record.claimCount === undefined
                      ? "Unavailable"
                      : record.claimCount.toLocaleString()
                  }
                />
              </dl>
            )}
            {view === "financial" && (
              <dl>
                <ContextFact label="Source-reported cost" value={formatRecordCost(record)} />
              </dl>
            )}
            {view === "workflow" && (
              <div className="mt-5">
                <h3 className="font-semibold">Draft workflow — eleven-stage lifecycle reference</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Approved for prototype exploration only; stage names, ordering, and production
                  transition policy remain pending stakeholder approval. This is not this record's
                  progress. Every stage's record state is unavailable.
                </p>
                <ol className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {INVESTIGATION_STAGES.map((stage, index) => (
                    <li
                      key={stage}
                      className="rounded-lg border border-border bg-background p-3 text-sm"
                    >
                      <span className="font-medium">
                        {index + 1}. {stage}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        Record state unavailable
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}
      </section>
    </section>
  );
}

function ContextFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-3 min-w-0 rounded-lg border border-border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words font-medium">{value}</dd>
    </div>
  );
}
